import { Component } from '@theme/component';
import {
  localThreshold,
  remainingAmount,
  indicativeAmount,
  formatAmount,
} from '@theme/free-shipping-guard';

/**
 * Free shipping threshold line, local-currency path.
 *
 * Liquid renders the element `hidden` with EUR amounts and the cart total in
 * the cart currency. This component reads window.Shopify.currency (injected by
 * Shopify on every storefront page), applies the guard rails and reveals the
 * line. Any doubt keeps it hidden.
 *
 * The cart drawer is morphed on every cart update and on every page load
 * (assets/section-hydration.js re-renders the header): the element may be
 * inserted (connectedCallback), or kept and updated in place. In the latter
 * case the morph copies attributes first (attributeChangedCallback fires while
 * the children still hold the previous state) and then syncs the children back
 * to the server markup (empty text, state "pending"). Recomputing synchronously
 * from attributeChangedCallback therefore gets overwritten; the update is
 * deferred to a microtask, which runs once the whole synchronous morph is done.
 * (Component#updatedCallback is not usable here: morph.js hands the parsed,
 * non-upgraded node to onAfterUpdate, so it never reaches this element.)
 *
 * Targets are queried directly rather than through `refs`: the popover slots
 * live inside a nested <anchored-popover-component>, and `refs` is refreshed
 * asynchronously after a morph.
 *
 * @extends Component
 */
class FreeShippingNotice extends Component {
  static observedAttributes = ['data-cart-total', 'hidden'];

  #updateScheduled = false;

  connectedCallback() {
    super.connectedCallback();
    this.#update();
  }

  /** Coalesces updates and runs them after the current synchronous morph. */
  #scheduleUpdate() {
    if (this.#updateScheduled) return;
    this.#updateScheduled = true;
    queueMicrotask(() => {
      this.#updateScheduled = false;
      if (this.isConnected) this.#update();
    });
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback?.(name, oldValue, newValue);
    if (!this.isConnected || oldValue === newValue) return;
    // `hidden` re-applied by a morph while the total did not change: recompute.
    if (name === 'hidden' && newValue === null) return;
    this.#scheduleUpdate();
  }

  #update() {
    // EUR path: Liquid rendered the final state, nothing to do.
    if (!this.hasAttribute('data-threshold-eur')) return;

    const currency = this.dataset.currency ?? '';
    const locale = this.dataset.locale || undefined;
    const shopifyCurrency = /** @type {{ active?: string, rate?: string | number } | undefined} */ (
      window.Shopify?.currency
    );
    if (!shopifyCurrency || shopifyCurrency.active !== currency) {
      this.hidden = true;
      return;
    }

    const rate = Number(shopifyCurrency.rate);
    const thresholdEur = Number(this.dataset.thresholdEur);
    const threshold = localThreshold(thresholdEur, currency, rate);
    if (threshold === null) {
      this.hidden = true;
      return;
    }

    const cartTotalCents = Number(this.dataset.cartTotal);
    if (!Number.isFinite(cartTotalCents)) {
      this.hidden = true;
      return;
    }

    const line = /** @type {HTMLElement | null} */ (this.querySelector('[ref="line"]'));
    const text = this.querySelector('[ref="text"]');
    if (!line || !text) {
      this.hidden = true;
      return;
    }
    const thresholdSlot = this.querySelector('[data-slot="threshold"]');
    const flatRateSlot = this.querySelector('[data-slot="flat-rate"]');

    const remaining = remainingAmount(threshold, cartTotalCents);
    if (remaining > 0) {
      text.textContent = (this.dataset.textRemaining ?? '').replace(
        '__AMOUNT__',
        formatAmount(remaining, currency, locale)
      );
      line.dataset.state = 'remaining';
    } else {
      text.textContent = this.dataset.textUnlocked ?? '';
      line.dataset.state = 'unlocked';
    }

    if (thresholdSlot) {
      thresholdSlot.textContent = `${formatAmount(threshold, currency, locale)} (${formatAmount(thresholdEur, 'EUR', locale)})`;
    }
    if (flatRateSlot) {
      const flatRateEur = Number(this.dataset.flatRateEur);
      const flatRateLocal = indicativeAmount(flatRateEur, currency, rate);
      flatRateSlot.textContent =
        flatRateLocal === null
          ? formatAmount(flatRateEur, 'EUR', locale)
          : `${formatAmount(flatRateEur, 'EUR', locale)} (≈ ${formatAmount(flatRateLocal, currency, locale)})`;
    }

    this.hidden = false;
  }
}

if (!customElements.get('free-shipping-notice')) {
  customElements.define('free-shipping-notice', FreeShippingNotice);
}
